/**
 * Transform Section Component
 * Displays and edits SceneObject transform properties (position, rotation, scale)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from 'trace-viewer';

/** Local {x, y, z} object type for Babylon.js Vector3-like values */
interface XYZ { x: number; y: number; z: number }
import { getUndoManager, TransformCommand, getTransformBindings, type TransformSnapshot, type TransformBindings, type BindableTransformChannel, type TransformAxis } from 'trace-viewer/editor';
import { routeDragEndCapture } from '../undo-gizmo-service';
import { TransformBindingChip } from './transform-binding-chip';

/**
 * Record a typed edit the panel has already applied, from `before`: route it as
 * a gizmo drag-end is (keyframe, page, state), else fold it into Base and push
 * a TransformCommand. Typed edits otherwise reach neither. Nothing is recorded
 * while an undo or redo replays.
 */
export function commitTypedTransform(obj: SceneObject, before: TransformSnapshot): void {
    if (getUndoManager().isUndoRedoing) return;
    // Skip no-op edits — the live-while-typing inputs re-fire on every
    // keystroke and on blur, so without this a blur after no change (or a
    // repeated value) would trigger a redundant keyframe capture / undo
    // entry. Matches the gizmo path's `changed` guard.
    const after = TransformCommand.capture(obj);
    if (!transformChanged(before, after)) return;
    const capturedIntoKeyframe = routeDragEndCapture(obj, before);
    if (!capturedIntoKeyframe) {
        getUndoManager().push(new TransformCommand(obj, before, after));
    }
}

/** True if any position/rotation/scale component differs between two snapshots. */
function transformChanged(a: TransformSnapshot, b: TransformSnapshot): boolean {
    return a.position.some((v, i) => v !== b.position[i])
        || a.rotation.some((v, i) => v !== b.rotation[i])
        || a.scale.some((v, i) => v !== b.scale[i]);
}

// ============================================================================
// Shared Styles
// ============================================================================

const styles = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '100px',
        flexShrink: 0,
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    } as React.CSSProperties,
};

// ============================================================================
// Vec3Field Component
// ============================================================================

interface Vec3FieldProps {
    label: string;
    value: XYZ;
    onChange: (value: XYZ) => void;
    onFocusChange?: (focused: boolean) => void;
    /** Per-axis property bindings on the base transform (null = literal). A
     *  bound axis is driven by its property: its input is read-only and shows
     *  the resolved value. */
    bindings?: [string | null, string | null, string | null];
    /** Renders the binding chip after an axis input. */
    renderChip?: (axis: TransformAxis) => React.ReactNode;
}

const AXES = ['x', 'y', 'z'] as const;
const AXIS_COLOR = { x: '#f88', y: '#8f8', z: '#88f' } as const;

const Vec3Field: React.FC<Vec3FieldProps> = ({ label, value, onChange, onFocusChange, bindings, renderChip }) => {
    const vec = value || { x: 0, y: 0, z: 0 };
    // Track which axis is being edited and its local string value
    const [editingAxis, setEditingAxis] = useState<'x' | 'y' | 'z' | null>(null);
    const [localValue, setLocalValue] = useState<string>('');

    const handleFocus = (axis: 'x' | 'y' | 'z') => {
        setEditingAxis(axis);
        setLocalValue(vec[axis].toFixed(2));
        onFocusChange?.(true);
    };

    const handleBlur = () => {
        if (editingAxis) {
            // Apply the final value on blur
            const numValue = parseFloat(localValue);
            if (!isNaN(numValue)) {
                onChange({ ...vec, [editingAxis]: numValue });
            }
        }
        setEditingAxis(null);
        setLocalValue('');
        onFocusChange?.(false);
    };

    const handleChange = (axis: 'x' | 'y' | 'z', stringValue: string) => {
        if (editingAxis === axis) {
            setLocalValue(stringValue);
            // Also apply changes live while typing (for immediate visual feedback)
            const numValue = parseFloat(stringValue);
            if (!isNaN(numValue)) {
                onChange({ ...vec, [axis]: numValue });
            }
        } else {
            // Direct change without focus (shouldn't happen normally)
            const numValue = parseFloat(stringValue);
            if (!isNaN(numValue)) {
                onChange({ ...vec, [axis]: numValue });
            }
        }
    };

    // Get display value: use local value if editing, otherwise use the prop value
    const getDisplayValue = (axis: 'x' | 'y' | 'z') => {
        if (editingAxis === axis) {
            return localValue;
        }
        return vec[axis].toFixed(2);
    };

    return (
        <div style={styles.fieldRow}>
            <span style={styles.label}>{label}</span>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {AXES.map((axis, i) => {
                    const bound = bindings?.[i] ?? null;
                    return (
                        <React.Fragment key={axis}>
                            <span style={{ color: AXIS_COLOR[axis], fontSize: '11px' }}>{axis.toUpperCase()}</span>
                            <input
                                type="number"
                                value={getDisplayValue(axis)}
                                onChange={(e) => handleChange(axis, e.target.value)}
                                onFocus={() => handleFocus(axis)}
                                onBlur={handleBlur}
                                step={0.1}
                                readOnly={!!bound}
                                title={bound ? `Follows $${bound}` : undefined}
                                style={{ ...styles.input, width: '62px', ...(bound ? { color: '#b794f6', borderColor: '#b794f640' } : {}) }}
                            />
                            {renderChip?.(i as TransformAxis)}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

// ============================================================================
// TransformSection Component
// ============================================================================

export interface TransformSectionProps {
    sceneObject: SceneObject | null;
}

export const TransformSection: React.FC<TransformSectionProps> = ({ sceneObject }) => {
    const [expanded, setExpanded] = useState(true);
    const [position, setPosition] = useState<XYZ>({ x: 0, y: 0, z: 0 });
    const [rotation, setRotation] = useState<XYZ>({ x: 0, y: 0, z: 0 });
    const [scale, setScale] = useState<XYZ>({ x: 1, y: 1, z: 1 });
    // Track if any input is focused to pause polling
    const [isEditing, setIsEditing] = useState(false);
    // Base-transform bindings ({deckHeight} on an axis) — read from the
    // object's Properties; refreshed with the pose and after chip actions.
    const [bindings, setBindings] = useState<TransformBindings>({ position: [null, null, null], scale: [null, null, null] });
    const refreshBindings = useCallback(() => {
        const next: TransformBindings = sceneObject ? getTransformBindings(sceneObject) : { position: [null, null, null], scale: [null, null, null] };
        // Polled at 10fps — only re-render when a binding actually changed.
        setBindings(prev => JSON.stringify(prev) === JSON.stringify(next) ? prev : next);
    }, [sceneObject]);

    // Read Euler rotation (degrees) from the transform, handling both quaternion and Euler modes
    const readRotationDegrees = useCallback((transform: { rotation: { x: number; y: number; z: number }; rotationQuaternion: { toEulerAngles(): { x: number; y: number; z: number } } | null }) => {
        const toDeg = 180 / Math.PI;
        if (transform.rotationQuaternion) {
            const euler = transform.rotationQuaternion.toEulerAngles();
            return { x: euler.x * toDeg, y: euler.y * toDeg, z: euler.z * toDeg };
        }
        return { x: transform.rotation.x * toDeg, y: transform.rotation.y * toDeg, z: transform.rotation.z * toDeg };
    }, []);

    // Load transform from scene object
    const loadTransform = useCallback(() => {
        if (!sceneObject?.transform) return;

        const transform = sceneObject.transform;
        setPosition({
            x: transform.position.x,
            y: transform.position.y,
            z: transform.position.z,
        });
        setRotation(readRotationDegrees(transform));
        setScale({
            x: transform.scaling.x,
            y: transform.scaling.y,
            z: transform.scaling.z,
        });
    }, [sceneObject, readRotationDegrees]);

    // Load transform when scene object changes
    useEffect(() => {
        loadTransform();
        refreshBindings();
    }, [loadTransform, refreshBindings]);

    // Poll for transform changes (handles gizmo manipulation, etc.)
    // Skip polling when user is editing to prevent overwriting their input
    useEffect(() => {
        if (!sceneObject?.transform || isEditing) return;

        const updateTransformFromScene = () => {
            const transform = sceneObject.transform;
            if (!transform) return;

            // Update position if changed
            const newPos = {
                x: transform.position.x,
                y: transform.position.y,
                z: transform.position.z,
            };
            setPosition(prev => {
                if (Math.abs(prev.x - newPos.x) > 0.001 ||
                    Math.abs(prev.y - newPos.y) > 0.001 ||
                    Math.abs(prev.z - newPos.z) > 0.001) {
                    return newPos;
                }
                return prev;
            });

            // Update rotation if changed (convert to degrees)
            const newRot = readRotationDegrees(transform);
            setRotation(prev => {
                if (Math.abs(prev.x - newRot.x) > 0.1 ||
                    Math.abs(prev.y - newRot.y) > 0.1 ||
                    Math.abs(prev.z - newRot.z) > 0.1) {
                    return newRot;
                }
                return prev;
            });

            // Update scale if changed
            const newScale = {
                x: transform.scaling.x,
                y: transform.scaling.y,
                z: transform.scaling.z,
            };
            setScale(prev => {
                if (Math.abs(prev.x - newScale.x) > 0.001 ||
                    Math.abs(prev.y - newScale.y) > 0.001 ||
                    Math.abs(prev.z - newScale.z) > 0.001) {
                    return newScale;
                }
                return prev;
            });
        };

        // Poll at 10fps for smooth updates during gizmo manipulation
        const intervalId = setInterval(() => { updateTransformFromScene(); refreshBindings(); }, 100);

        return () => clearInterval(intervalId);
    }, [sceneObject, isEditing, readRotationDegrees, refreshBindings]);

    const onBindingChanged = useCallback(() => { refreshBindings(); loadTransform(); }, [refreshBindings, loadTransform]);
    const chipFor = useCallback((channel: BindableTransformChannel) => (axis: TransformAxis) => sceneObject ? (
        <TransformBindingChip
            sceneObject={sceneObject}
            channel={channel}
            axis={axis}
            boundTo={bindings[channel][axis]}
            onChanged={onBindingChanged}
        />
    ) : null, [sceneObject, bindings, onBindingChanged]);

    // Handle position change
    const handlePositionChange = useCallback((value: XYZ) => {
        if (!sceneObject?.transform) return;
        const before = TransformCommand.capture(sceneObject);
        setPosition(value);
        sceneObject.transform.position.x = value.x;
        sceneObject.transform.position.y = value.y;
        sceneObject.transform.position.z = value.z;
        commitTypedTransform(sceneObject, before);
    }, [sceneObject]);

    // Handle rotation change (input is degrees, convert to radians)
    const handleRotationChange = useCallback((value: XYZ) => {
        if (!sceneObject?.transform) return;
        const before = TransformCommand.capture(sceneObject);
        setRotation(value);
        const toRad = Math.PI / 180;
        const rx = value.x * toRad;
        const ry = value.y * toRad;
        const rz = value.z * toRad;
        if (sceneObject.transform.rotationQuaternion) {
            Quaternion.FromEulerAnglesToRef(rx, ry, rz, sceneObject.transform.rotationQuaternion);
        } else {
            sceneObject.transform.rotation.x = rx;
            sceneObject.transform.rotation.y = ry;
            sceneObject.transform.rotation.z = rz;
        }
        commitTypedTransform(sceneObject, before);
    }, [sceneObject]);

    // Handle scale change
    const handleScaleChange = useCallback((value: XYZ) => {
        if (!sceneObject?.transform) return;
        const before = TransformCommand.capture(sceneObject);
        setScale(value);
        sceneObject.transform.scaling.x = value.x;
        sceneObject.transform.scaling.y = value.y;
        sceneObject.transform.scaling.z = value.z;
        commitTypedTransform(sceneObject, before);
    }, [sceneObject]);

    if (!sceneObject) return null;

    return (
        <div style={{
            marginBottom: '8px',
            backgroundColor: '#2d2d2d',
            borderRadius: '4px',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#383838',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <span style={{
                    marginRight: '8px',
                    fontSize: '10px',
                    color: '#888',
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                }}>
                    ▶
                </span>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 500 }}>
                    Transform
                </span>
            </div>

            {/* Body */}
            {expanded && (
                <div style={{ padding: '12px' }}>
                    <Vec3Field label="Position" value={position} onChange={handlePositionChange} onFocusChange={setIsEditing}
                        bindings={bindings.position} renderChip={chipFor('position')} />
                    <Vec3Field label="Rotation" value={rotation} onChange={handleRotationChange} onFocusChange={setIsEditing} />
                    <Vec3Field label="Scale" value={scale} onChange={handleScaleChange} onFocusChange={setIsEditing}
                        bindings={bindings.scale} renderChip={chipFor('scale')} />
                </div>
            )}
        </div>
    );
};
